// @ts-nocheck
import { useEffect, useState } from 'react';
import { trackEvent } from '@/lib/posthog';
import {
  useGetPipelinesQuery,
  useCreatePipelineMutation,
  useEditPipelineMutation,
  useDeletePipelineMutation,
  useMoveDealsAndDeletePipelineMutation,
  useCreateDealStageMutation,
  useUpdateDealStageMutation,
  useDeleteDealStageMutation,
  type Pipeline as GQLPipeline,
  type DealStage as GQLDealStage,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from './use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';
import { defaultPipelines, sortPipelineStages } from '@/data/pipelines';
import type { Pipeline, PipelineStage } from '@/data/pipelines';
import { usePipelines as usePipelinesContext } from '@/contexts/PipelinesContext';

/**
 * Maps hex color codes to color names for backend storage
 */
const colorCodeToName: Record<string, string> = {
  '#93c5fd': 'blue',
  '#7dd3fc': 'sky',
  '#5eead4': 'teal',
  '#fcd34d': 'amber',
  '#fdba74': 'orange',
  '#f9a8d4': 'pink',
  '#c4b5fd': 'violet',
  '#86efac': 'green',
  '#fca5a5': 'red',
};

function getColorName(hexCode: string): string {
  return colorCodeToName[hexCode.toLowerCase()] || 'blue';
}

/**
 * Maps GraphQL Pipeline to UI Pipeline format
 */
function mapPipelineFromGraphQL(gqlPipeline: GQLPipeline): Pipeline {
  const stages: PipelineStage[] = gqlPipeline.stages?.edges
    ?.map(edge => {
      const node = edge?.node as GQLDealStage;
      if (!node || node.deleted) return null; // Filter out deleted stages

      // Backend stores probability as 0.0-1.0, UI expects 0-100
      const probability = node.probability ?? 0.5;
      const pct = Math.round(probability * 100);

      return {
        id: node.id,
        externalId: node.externalId || undefined,
        name: node.name,
        // Present only while the stage still carries Coffee's shipped wording;
        // the backend clears it on rename. Resolved for display by stageLabel.
        i18nKey: node.i18nKey ?? null,
        pct,
        color: node.colorCode ?? '#c4b5fd',
        // Keep the backend terminal flags — sortPipelineStages uses them to pin
        // Closed Won / Closed Lost to the end regardless of percentage.
        won: node.won ?? undefined,
        lost: node.lost ?? undefined,
      };
    })
    .filter((s): s is PipelineStage => s !== null) ?? [];

  return {
    id: gqlPipeline.id,
    name: gqlPipeline.name,
    // Same contract as the stages above: present only while untouched.
    i18nKey: gqlPipeline.i18nKey ?? null,
    stages: sortPipelineStages(stages),
  };
}

/**
 * Hook for managing pipelines with backend integration
 *
 * Provides:
 * - Loading pipelines from backend
 * - Creating, editing, and deleting pipelines
 * - Creating, editing, and deleting stages
 * - Fallback to dummy data in Lovable Preview mode
 * 
 * In preview mode, uses PipelinesContext so state is shared across the app.
 */
export function usePipelines() {
  const workspaceId = useWorkspaceId();
  const isPreview = isLovablePreview();
  
  // Use shared context in preview mode so DealsPage and Settings share the same state
  const pipelinesCtx = usePipelinesContext();
  
  const [localPipelines, setLocalPipelines] = useState<Pipeline[]>(defaultPipelines);
  const [loading, setLoading] = useState(true);
  // True once the current pipelines were mapped from backend data (as opposed to the
  // dummy `defaultPipelines` fallback). Lets consumers tell a real pipeline id apart
  // from a dummy one — the ids can collide (a workspace's real pipeline can be "1").
  const [hasBackendPipelines, setHasBackendPipelines] = useState(false);

  // In preview mode, use the shared context; otherwise use local state backed by GraphQL
  const pipelines = isPreview ? pipelinesCtx.pipelines : localPipelines;
  const setPipelines = isPreview ? pipelinesCtx.setPipelines : setLocalPipelines;

  // GraphQL queries and mutations
  const { data, refetch } = useGetPipelinesQuery({
    variables: {
      filter: { workspaceId: workspaceId ?? undefined },
    },
    skip: !workspaceId || isPreview,
  });

  const [createPipelineMutation] = useCreatePipelineMutation();
  const [editPipelineMutation] = useEditPipelineMutation();
  const [deletePipelineMutation] = useDeletePipelineMutation();
  const [moveDealsAndDeleteMutation] = useMoveDealsAndDeletePipelineMutation();
  const [createStageMutation] = useCreateDealStageMutation();
  const [updateStageMutation] = useUpdateDealStageMutation();
  const [deleteStageMutation] = useDeleteDealStageMutation();

  // Switching workspaces re-runs the query with new variables, but `data` holds the
  // previous workspace's result until then. Flip back to loading so consumers don't
  // act on another workspace's pipelines (e.g. the deals Stage filter deriving open
  // stages from stale data and recording them for the new workspace's pipeline).
  useEffect(() => {
    if (isPreview) return;
    setLoading(true);
    setHasBackendPipelines(false);
  }, [workspaceId]);

  // Load pipelines from backend
  useEffect(() => {
    if (isPreview) {
      setLoading(false);
      return;
    }

    // If we have data (even if empty), we're done loading
    if (data !== undefined) {
      if (data?.pipelines?.edges && data.pipelines.edges.length > 0) {
        const loadedPipelines = data.pipelines.edges
          .map(edge => edge?.node)
          .filter((node): node is GQLPipeline => node !== null && !node.deleted)
          .map(pipeline => mapPipelineFromGraphQL(pipeline));

        setPipelines(loadedPipelines.length > 0 ? loadedPipelines : defaultPipelines);
        setHasBackendPipelines(loadedPipelines.length > 0);
      } else {
        // No pipelines from backend, use defaults
        setPipelines(defaultPipelines);
        setHasBackendPipelines(false);
      }
      setLoading(false);
    }
  }, [data]);

  /**
   * Create a new pipeline
   */
  const createPipeline = async (name: string, initialStages?: PipelineStage[]) => {
    if (isPreview) {
      // Local-only in preview mode
      const newPipeline: Pipeline = {
        id: `p${Date.now()}`,
        name,
        stages: initialStages ?? [
          { id: `s${Date.now()}a`, name: 'Qualification', pct: 10, color: '#93c5fd' },
          { id: `s${Date.now()}b`, name: 'Closed Won', pct: 100, color: '#86efac' },
        ],
      };
      setPipelines(prev => [...prev, newPipeline]);
      return newPipeline;
    }

    if (!workspaceId) throw new Error('No workspace selected');

    const result = await createPipelineMutation({
      variables: {
        input: {
          workspaceId,
          name,
        },
      },
    });

    const createdPipeline = result.data?.createPipeline?.pipeline;
    if (!createdPipeline) throw new Error('Failed to create pipeline');
    trackEvent('workspace_customized', { action: 'pipeline_created' });

    // Create initial stages if provided
    if (initialStages && initialStages.length > 0) {
      for (let i = 0; i < initialStages.length; i++) {
        const stage = initialStages[i];
        await createStageMutation({
          variables: {
            input: {
              workspaceId,
              pipelineId: createdPipeline.id,
              name: stage.name,
              probability: stage.pct / 100, // Convert UI percentage to backend decimal
              // Explicit won/lost so the backend never derives them from
              // probability. Absent means open — the stage name is not consulted.
              isWon: stage.won ?? false,
              isLost: stage.lost ?? false,
              color: getColorName(stage.color), // Required field - text name like "blue"
              colorCode: stage.color, // Hex code like "#93c5fd"
              externalId: `stage-${Date.now()}-${i}`, // Required field - generate unique ID
              order: i,
            },
          },
        });
      }
    }

    // Refetch to get the complete pipeline with stages
    const { data: refetchedData } = await refetch();
    const refetchedPipeline = refetchedData?.pipelines?.edges
      ?.map(e => e?.node)
      .filter((node): node is GQLPipeline => node !== null)
      .find(p => p.id === createdPipeline.id);

    return refetchedPipeline ? mapPipelineFromGraphQL(refetchedPipeline) : mapPipelineFromGraphQL(createdPipeline as unknown as GQLPipeline);
  };

  /**
   * Edit pipeline name
   */
  const editPipeline = async (pipelineId: string, name: string) => {
    if (isPreview) {
      setPipelines(prev => prev.map(p => p.id === pipelineId ? { ...p, name } : p));
      return;
    }

    if (!workspaceId) throw new Error('No workspace selected');

    await editPipelineMutation({
      variables: {
        input: {
          pipelineId,
          workspaceId,
          name,
        },
      },
    });

    await refetch();
  };

  /**
   * Delete pipeline
   */
  const deletePipeline = async (pipelineId: string) => {
    if (isPreview) {
      setPipelines(prev => {
        const next = prev.filter(p => p.id !== pipelineId);
        return next.length === 0 ? prev : next;
      });
      return;
    }

    if (!workspaceId) throw new Error('No workspace selected');

    await deletePipelineMutation({
      variables: {
        input: {
          pipelineId,
          workspaceId,
          // Never take a pipeline's deals down with it. Settings blocks the delete
          // while deals exist, and the backend refuses this input if any remain, so
          // a pipeline only ever gets deleted empty.
          deleteDeals: false,
        },
      },
    });

    await refetch();
  };

  /**
   * Move deals from one pipeline to another and delete the source pipeline
   */
  const moveDealsAndDeletePipeline = async (fromPipelineId: string, toPipelineId: string) => {
    if (isPreview) {
      setPipelines(prev => {
        const next = prev.filter(p => p.id !== fromPipelineId);
        return next.length === 0 ? prev : next;
      });
      return;
    }

    if (!workspaceId) throw new Error('No workspace selected');

    await moveDealsAndDeleteMutation({
      variables: {
        input: {
          workspaceId,
          fromPipelineId,
          toPipelineId,
          stageMappings: [], // Empty array means no stage mappings - deals will be moved without stage conversion
        },
      },
    });

    await refetch();
  };

  /**
   * Create a new stage in a pipeline
   */
  const createStage = async (pipelineId: string, stage: Omit<PipelineStage, 'id'>) => {
    if (isPreview) {
      const newStage: PipelineStage = {
        id: `s${Date.now()}`,
        ...stage,
      };
      setPipelines(prev => prev.map(p =>
        p.id !== pipelineId ? p : { ...p, stages: sortPipelineStages([...p.stages, newStage]) }
      ));
      return;
    }

    if (!workspaceId) throw new Error('No workspace selected');

    const pipeline = pipelines.find(p => p.id === pipelineId);
    if (!pipeline) throw new Error('Pipeline not found');

    await createStageMutation({
      variables: {
        input: {
          workspaceId,
          pipelineId,
          name: stage.name,
          probability: stage.pct / 100, // Convert UI percentage (0-100) to backend decimal (0-1)
          // Always send explicit won/lost so the backend never guesses from
          // probability. Absent means open — the stage name is not consulted.
          isWon: stage.won ?? false,
          isLost: stage.lost ?? false,
          color: getColorName(stage.color), // Required field - text name like "blue"
          colorCode: stage.color, // Hex code like "#93c5fd"
          externalId: `stage-${Date.now()}`, // Required field - generate unique ID
          order: pipeline.stages.length,
        },
      },
    });

    await refetch();
  };

  /**
   * Update a stage
   */
  const updateStage = async (stageId: string, updates: Partial<PipelineStage>) => {
    if (isPreview) {
      setPipelines(prev => prev.map(p => ({
        ...p,
        stages: sortPipelineStages(
          p.stages.map(s => s.id === stageId ? { ...s, ...updates } : s)
        ),
      })));
      return;
    }

    if (!workspaceId) throw new Error('No workspace selected');

    await updateStageMutation({
      variables: {
        input: {
          id: stageId,
          ...(updates.name !== undefined && { name: updates.name }),
          ...(updates.pct !== undefined && { probability: updates.pct / 100 }), // Convert UI percentage to backend decimal
          // Explicit terminal-outcome flags (Settings Open/Won/Lost dropdown);
          // won/lost is no longer derived from probability on the backend.
          ...(updates.won !== undefined && { isWon: updates.won }),
          ...(updates.lost !== undefined && { isLost: updates.lost }),
          ...(updates.color !== undefined && {
            color: getColorName(updates.color), // Text name like "blue"
            colorCode: updates.color // Hex code like "#93c5fd"
          }),
        },
      },
    });

    await refetch();
  };

  /**
   * Delete a stage
   */
  const deleteStage = async (stageId: string, newStageId?: string) => {
    if (isPreview) {
      setPipelines(prev => prev.map(p => ({
        ...p,
        stages: p.stages.filter(s => s.id !== stageId),
      })));
      return;
    }

    if (!workspaceId) throw new Error('No workspace selected');

    await deleteStageMutation({
      variables: {
        input: {
          id: stageId,
          newStageId,
        },
      },
    });

    await refetch();
  };

  /**
   * Reorder stages within a pipeline
   */
  const reorderStages = async (pipelineId: string, fromId: string, toId: string) => {
    if (isPreview) {
      setPipelines(prev => prev.map(p => {
        if (p.id !== pipelineId) return p;
        const stages = [...p.stages];
        const fromIdx = stages.findIndex(s => s.id === fromId);
        const toIdx = stages.findIndex(s => s.id === toId);
        if (fromIdx === -1 || toIdx === -1) return p;
        const [moved] = stages.splice(fromIdx, 1);
        stages.splice(toIdx, 0, moved);
        return { ...p, stages };
      }));
      return;
    }

    // Update order for all affected stages
    const pipeline = pipelines.find(p => p.id === pipelineId);
    if (!pipeline) return;

    const stages = [...pipeline.stages];
    const fromIdx = stages.findIndex(s => s.id === fromId);
    const toIdx = stages.findIndex(s => s.id === toId);
    if (fromIdx === -1 || toIdx === -1) return;

    const [moved] = stages.splice(fromIdx, 1);
    stages.splice(toIdx, 0, moved);

    // Update order for all stages
    for (let i = 0; i < stages.length; i++) {
      await updateStageMutation({
        variables: {
          input: {
            id: stages[i].id,
            order: i,
          },
        },
      });
    }

    await refetch();
  };

  return {
    pipelines,
    setPipelines,
    loading,
    hasBackendPipelines,
    createPipeline,
    editPipeline,
    deletePipeline,
    moveDealsAndDeletePipeline,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
    refetch,
  };
}
